import { useEffect, useState, useRef, useCallback } from "react";
import styles from './logs.module.css';
import { useAuth0 } from "@auth0/auth0-react";
import Loader from "../../components/Loader";
import { Helmet } from "react-helmet";
import { Typography } from "@mui/material";
import { ChevronDown, ChevronUp, Code, Home, Search } from "react-feather";
import Footer from "../../components/Footer";
import { TableLoaderSmall } from "../../components/LoaderC";
import axios from 'axios';
import withCustomAuthenticationRequired from "../../auth/withCustomAuthenticationRequired";

const CHUNK_SIZE = 20;

const NewlineText = (props) => {
  let text = props.text || "";
  let newText = text.replace(/(?:\r\n|\r|\n)/g, '<br>');
  return <div style={{ whiteSpace: "pre-wrap" }} dangerouslySetInnerHTML={{ __html: newText }}></div>;
};

const Logs = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [contentLoading, setContentLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [allData, setAllData] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [allTotal, setAllTotal] = useState(0);
  const [parkingGateTotal, setParkingGateTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [quickFilter, setQuickFilter] = useState("all");

  const sentinelRef = useRef(null);
  const requestSeq = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  const changeSortOrder = (item) => {
    if (item === orderBy) {
      setOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setOrder("asc");
      setOrderBy(item);
    }
  };

  const fetchAuxiliaryCount = useCallback(async (token, currentSearch, timezone_offset_minutes) => {
    try {
      if (quickFilter === "all") {
        const parkingSearch = currentSearch ? `${currentSearch} parking gate` : "parking gate";
        const res = await axios.get(
          `${process.env.REACT_APP_LOGS_API_URL}v1/logs-new?page=1&limit=1&search=${encodeURIComponent(parkingSearch)}&timezone_offset=${timezone_offset_minutes}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setParkingGateTotal(res.data?.total || 0);
      } else {
        const res = await axios.get(
          `${process.env.REACT_APP_LOGS_API_URL}v1/logs-new?page=1&limit=1&search=${encodeURIComponent(currentSearch)}&timezone_offset=${timezone_offset_minutes}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAllTotal(res.data?.total || 0);
      }
    } catch (err) {
      console.error("Error fetching auxiliary count:", err);
    }
  }, [quickFilter]);

  const fetchLogs = useCallback(async (currentOffset, replace) => {
    const seq = ++requestSeq.current;
    const token = await getAccessTokenSilently({
      audience: process.env.REACT_APP_AUTH0_AUDIENCE
    });

    const isInitialLoad = replace && currentOffset === 0 && !hasLoadedOnceRef.current;
    let activeIndicator = 'loadingMore';

    if (isInitialLoad) {
      activeIndicator = 'contentLoading';
      setContentLoading(true);
    } else if (replace) {
      activeIndicator = 'refetching';
      setRefetching(true);
    } else {
      setLoadingMore(true);
    }

    const timezone_offset_minutesdata = new Date().getTimezoneOffset();
    const timezone_offset_minutes =
      timezone_offset_minutesdata === 0 ? 0 : -timezone_offset_minutesdata;

    const effectiveSearch = quickFilter === "parking_gate"
      ? (debouncedSearch ? `${debouncedSearch} parking gate` : "parking gate")
      : debouncedSearch;

    const pageNum = Math.floor(currentOffset / CHUNK_SIZE) + 1;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LOGS_API_URL}v1/logs-new?page=${pageNum}&offset=${currentOffset}&limit=${CHUNK_SIZE}&search=${encodeURIComponent(effectiveSearch)}&sort_by=${orderBy}&sort_order=${order}&timezone_offset=${timezone_offset_minutes}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (seq !== requestSeq.current) return;

      const newChunk = response.data.data || [];
      const total = response.data.total || 0;

      setAllData(prev => replace ? newChunk : [...prev, ...newChunk]);
      setOffset(currentOffset + newChunk.length);

      if (quickFilter === "all") {
        setAllTotal(total);
      } else {
        setParkingGateTotal(total);
      }

      if (replace && currentOffset === 0) {
        fetchAuxiliaryCount(token, debouncedSearch, timezone_offset_minutes);
      }

      const moreAvailable = response.data?.meta?.has_more !== undefined
        ? Boolean(response.data.meta.has_more)
        : (currentOffset + newChunk.length < total);

      setHasMore(moreAvailable);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      if (seq === requestSeq.current) {
        if (activeIndicator === 'contentLoading') setContentLoading(false);
        else if (activeIndicator === 'refetching') setRefetching(false);
        else setLoadingMore(false);
      }
    }
  }, [getAccessTokenSilently, debouncedSearch, quickFilter, orderBy, order, fetchAuxiliaryCount]);

  // Debounce search text input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(handler);
  }, [search]);

  // Refetch logs when search, quickFilter, or sorting changes
  useEffect(() => {
    const delay = debouncedSearch ? 400 : 0;
    const timer = setTimeout(() => {
      fetchLogs(0, true);
    }, delay);
    return () => clearTimeout(timer);
  }, [debouncedSearch, quickFilter, orderBy, order, fetchLogs]);

  // Infinite Scroll: Intersection Observer on sentinelRef
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !contentLoading && !refetching) {
        fetchLogs(offset, false);
      }
    }, { threshold: 0.1 });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, contentLoading, offset, fetchLogs, refetching]);

  const activeTotal = quickFilter === "all" ? allTotal : parkingGateTotal;
  const loadedCount = Math.min(allData.length, activeTotal);
  const remainingCount = Math.max(0, activeTotal - loadedCount);

  return (
    <>
      <Helmet title="Logs" />

      {/* Page Title & Breadcrumb */}
      <div className={`${styles.pageTitleWrap}`}>
        <Typography display="inline" className={`${styles.pageTitle} ${styles.Big}`}>Logs</Typography>
        <nav aria-label="breadcrumb">
          <ol className={`${styles.breadcrumb}`}>
            <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Logs</li>
          </ol>
        </nav>
      </div>

      <div className={`${styles.BodyCon}`}>
        {/* Sticky Filter & Counter Controls Header */}
        <div className={styles.stickyHeaderContainer}>
          {/* Search Control Row */}
          <div className={styles.filterControlsRow}>
            <div className={styles.searchWrapper}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                value={search}
                placeholder="Search logs by action, description or user..."
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Quick Filter Pills (Bubble Filter) & Load Status Counter */}
          <div className={styles.quickFiltersRow}>
            <div className={styles.pillsGroup}>
              <button
                className={`${styles.filterPill} ${quickFilter === "all" ? styles.filterPillActive : ""}`}
                onClick={() => setQuickFilter("all")}
              >
                All <span className={styles.pillCount}>{allTotal.toLocaleString()}</span>
              </button>
              <button
                className={`${styles.filterPill} ${quickFilter === "parking_gate" ? styles.filterPillActive : ""}`}
                onClick={() => setQuickFilter("parking_gate")}
              >
                Parking gate <span className={styles.pillCount}>{parkingGateTotal.toLocaleString()}</span>
              </button>
            </div>

            <div className={styles.loadStatusCounter}>
              <span>Showing <strong>{loadedCount.toLocaleString()}</strong> of <strong>{activeTotal.toLocaleString()}</strong> logs</span>
              <span className={styles.remainingBadge}>
                {remainingCount > 0 ? `${remainingCount.toLocaleString()} remaining` : "All loaded"}
              </span>
            </div>
          </div>
        </div>

        {/* Main Table Card */}
        <div className={`${styles.mainCardDiv}`}>
          <div className={`${styles.mainCard}`}>
            {contentLoading && (
              <div className="Loader TableLoader">
                <TableLoaderSmall />
              </div>
            )}

            {!contentLoading && (
              <div className={styles.controlPane}>
                <div style={{ position: 'relative', opacity: refetching ? 0.5 : 1, transition: 'opacity 0.15s ease' }}>
                  {allData.length > 0 && (
                    <div className={styles.tableContainer}>
                      <table className={styles.logsTable}>
                        <thead>
                          <tr>
                            <th onClick={() => changeSortOrder("created_at")} className={styles.clickableHeader}>
                              <span className={styles.sortableHeader}>
                                Date
                                {orderBy === "created_at" && order === "asc" && <ChevronDown />}
                                {orderBy === "created_at" && order === "desc" && <ChevronUp />}
                                {orderBy !== "created_at" && <Code className={styles.Normal} />}
                              </span>
                            </th>
                            <th onClick={() => changeSortOrder("title")} className={styles.clickableHeader}>
                              <span className={styles.sortableHeader}>
                                Action Name
                                {orderBy === "title" && order === "asc" && <ChevronDown />}
                                {orderBy === "title" && order === "desc" && <ChevronUp />}
                                {orderBy !== "title" && <Code className={styles.Normal} />}
                              </span>
                            </th>
                            <th onClick={() => changeSortOrder("description")} className={styles.clickableHeader}>
                              <span className={styles.sortableHeader}>
                                Action Description
                                {orderBy === "description" && order === "asc" && <ChevronDown />}
                                {orderBy === "description" && order === "desc" && <ChevronUp />}
                                {orderBy !== "description" && <Code className={styles.Normal} />}
                              </span>
                            </th>
                            <th onClick={() => changeSortOrder("performed_by")} className={styles.clickableHeader}>
                              <span className={styles.sortableHeader}>
                                Performed By
                                {orderBy === "performed_by" && order === "asc" && <ChevronDown />}
                                {orderBy === "performed_by" && order === "desc" && <ChevronUp />}
                                {orderBy !== "performed_by" && <Code className={styles.Normal} />}
                              </span>
                            </th>
                            <th>Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allData.map((row) => {
                            const typeText = row?.is_tenant === 1 ? 'Tenant' : (row?.is_prospect === 1 ? 'Prospect' : 'User');
                            const typeClass = row?.is_tenant === 1 ? styles.typeTenant : (row?.is_prospect === 1 ? styles.typeProspect : styles.typeUser);

                            return (
                              <tr key={row?.id} className={styles.logsRow}>
                                <td>
                                  <div className={styles.dateMeta}>
                                    <span className={styles.dateTB}>{row?.date_str}</span>
                                    <span className={styles.dateTS}>{row?.time_str}{row?.time_str2 ? `, ${row?.time_str2}` : ''}</span>
                                  </div>
                                </td>
                                <td>
                                  <span className={styles.actionName}>{row?.title}</span>
                                </td>
                                <td>
                                  <div className={styles.actionDescription}>
                                    <NewlineText text={row?.description} />
                                  </div>
                                </td>
                                <td>
                                  <div className={styles.performedBy}>
                                    <NewlineText text={row?.performed_by} />
                                  </div>
                                </td>
                                <td>
                                  <span className={`${styles.typeBadge} ${typeClass}`}>
                                    {typeText}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}

                          {/* Sentinel row: entering viewport triggers loading next chunk */}
                          <tr ref={sentinelRef} style={{ height: 1 }}>
                            <td colSpan={5} style={{ padding: 0, border: 'none' }} />
                          </tr>

                          {loadingMore && (
                            <tr className={styles.scrollLoadingRow}>
                              <td colSpan={5}>
                                <div className={styles.scrollLoaderWrap}>
                                  <div className={styles.scrollSpinner} />
                                  <span>Loading more logs...</span>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Empty State */}
                  {allData.length === 0 && !refetching && (
                    <div className={styles.NoDataMain}>
                      <div className={styles.NoDataIMG}>
                        <img src="/static/img/no-prospect.png" alt="No Data" />
                      </div>
                      <p className={styles.NoDataText}>No logs found</p>
                      <p className={styles.NoDataTextSub}>Keep exploring! Try a different search term or filter option.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default withCustomAuthenticationRequired(Logs, {
  onRedirecting: () => <Loader />,
});